import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UserTable from './components/UserTable';
import UserForm from './components/UserForm';
import RoleTemplates from './components/RoleTemplates';
import ActivityLog from './components/ActivityLog';
import SecuritySettings from './components/SecuritySettings';
import UserStats from './components/UserStats';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Mock user data
  useEffect(() => {
    const mockUsers = [
      {
        id: 1,
        name: 'Sarah Mitchell',
        email: 'sarah.mitchell@carbonrouter.com',
        role: 'admin',
        status: 'active',
        department: 'it',
        phone: '+1 (555) 123-4567',
        location: 'London, UK',
        permissions: ['fleet_view', 'fleet_edit', 'vessel_manage', 'user_create', 'user_edit', 'user_delete', 'system_settings'],
        lastLogin: '2 hours ago',
        lastLoginDate: new Date(Date.now() - 2 * 60 * 60 * 1000),
        createdAt: new Date('2024-01-15')
      },
      {
        id: 2,
        name: 'Michael Rodriguez',
        email: 'michael.rodriguez@carbonrouter.com',
        role: 'chartering',
        status: 'active',
        department: 'commercial',
        phone: '+1 (555) 234-5678',
        location: 'Singapore',
        permissions: ['fleet_view', 'voyage_plan', 'voyage_edit', 'route_optimize', 'emissions_calculate'],
        lastLogin: '1 day ago',
        lastLoginDate: new Date(Date.now() - 24 * 60 * 60 * 1000),
        createdAt: new Date('2024-02-20')
      },
      {
        id: 3,
        name: 'Emma Wilson',
        email: 'emma.wilson@carbonrouter.com',
        role: 'finance',
        status: 'active',
        department: 'finance',
        phone: '+1 (555) 345-6789',
        location: 'New York, USA',
        permissions: ['fleet_view', 'eua_view', 'eua_trade', 'eua_forecast', 'compliance_reports'],
        lastLogin: '30 minutes ago',
        lastLoginDate: new Date(Date.now() - 30 * 60 * 1000),
        createdAt: new Date('2024-01-28')
      },
      {
        id: 4,
        name: 'David Brown',
        email: 'david.brown@carbonrouter.com',
        role: 'compliance',
        status: 'suspended',
        department: 'legal',
        phone: '+1 (555) 456-7890',
        location: 'Hamburg, Germany',
        permissions: ['fleet_view', 'emissions_calculate', 'compliance_reports', 'audit_trail'],
        lastLogin: '5 days ago',
        lastLoginDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        createdAt: new Date('2024-03-10')
      },
      {
        id: 5,
        name: 'Lisa Chen',
        email: 'lisa.chen@carbonrouter.com',
        role: 'operations',
        status: 'active',
        department: 'operations',
        phone: '+1 (555) 567-8901',
        location: 'Hong Kong',
        permissions: ['fleet_view', 'voyage_plan', 'vessel_manage', 'emissions_calculate'],
        lastLogin: '3 hours ago',
        lastLoginDate: new Date(Date.now() - 3 * 60 * 60 * 1000),
        createdAt: new Date('2024-02-05')
      },
      {
        id: 6,
        name: 'James Thompson',
        email: 'james.thompson@carbonrouter.com',
        role: 'technical',
        status: 'active',
        department: 'technical',
        phone: '+1 (555) 678-9012',
        location: 'Rotterdam, Netherlands',
        permissions: ['fleet_view', 'vessel_manage', 'emissions_calculate', 'compliance_reports'],
        lastLogin: '1 hour ago',
        lastLoginDate: new Date(Date.now() - 60 * 60 * 1000),
        createdAt: new Date('2024-01-12')
      },
      {
        id: 7,
        name: 'Maria Garcia',
        email: 'maria.garcia@carbonrouter.com',
        role: 'chartering',
        status: 'pending',
        department: 'commercial',
        phone: '+1 (555) 789-0123',
        location: 'Barcelona, Spain',
        permissions: ['fleet_view', 'voyage_plan', 'emissions_calculate'],
        lastLogin: 'Never',
        lastLoginDate: null,
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
      },
      {
        id: 8,
        name: 'Alex Johnson',
        email: 'alex.johnson@carbonrouter.com',
        role: 'finance',
        status: 'active',
        department: 'finance',
        phone: '+1 (555) 890-1234',
        location: 'Oslo, Norway',
        permissions: ['fleet_view', 'eua_view', 'eua_trade', 'compliance_reports'],
        lastLogin: '6 hours ago',
        lastLoginDate: new Date(Date.now() - 6 * 60 * 60 * 1000),
        createdAt: new Date('2024-03-01')
      }
    ];

    setTimeout(() => {
      setUsers(mockUsers);
      setIsLoading(false);
    }, 1000);
  }, []);

  const tabs = [
    { id: 'users', label: 'Users', icon: 'Users' },
    { id: 'roles', label: 'Role Templates', icon: 'Shield' },
    { id: 'activity', label: 'Activity Log', icon: 'Activity' },
    { id: 'security', label: 'Security', icon: 'Lock' },
    { id: 'stats', label: 'Statistics', icon: 'BarChart3' }
  ];

  const handleAddUser = () => {
    setEditingUser(null);
    setIsUserFormOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsUserFormOpen(true);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      setUsers(prev => prev?.filter(user => user?.id !== userId));
    }
  };

  const handleToggleStatus = (userId) => {
    setUsers(prev => prev?.map(user => 
      user?.id === userId 
        ? { ...user, status: user?.status === 'active' ? 'inactive' : 'active' }
        : user
    ));
  };

  const handleSaveUser = async (userData) => {
    if (editingUser) {
      // Update existing user
      setUsers(prev => prev?.map(user => 
        user?.id === editingUser?.id 
          ? { ...user, ...userData, id: editingUser?.id }
          : user
      ));
    } else {
      // Add new user
      const newUser = {
        ...userData,
        id: Math.max(...users?.map(u => u?.id)) + 1,
        lastLogin: 'Never',
        lastLoginDate: null,
        createdAt: new Date()
      };
      setUsers(prev => [...prev, newUser]);
    }
    setIsUserFormOpen(false);
    setEditingUser(null);
  };

  const handleApplyTemplate = (template) => {
    console.log('Applying template:', template);
    // Template application logic would go here
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <Icon name="Loader2" size={48} strokeWidth={2} className="mx-auto text-primary animate-spin mb-4" />
              <p className="text-muted-foreground">Loading user management...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>User Management - Carbon Cost Router</title>
        <meta name="description" content="Manage user accounts, roles, and security settings for the Carbon Cost Router platform" />
      </Helmet>
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">User Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage user accounts, roles, and security settings across your maritime carbon management platform
              </p>
            </div>
            
            {activeTab === 'users' && (
              <div className="mt-4 lg:mt-0">
                <Button onClick={handleAddUser}>
                  <Icon name="UserPlus" size={16} strokeWidth={2} className="mr-2" />
                  Add New User
                </Button>
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-border mb-8">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-maritime ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} strokeWidth={2} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'users' && (
              <UserTable
                users={users}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                onToggleStatus={handleToggleStatus}
              />
            )}

            {activeTab === 'roles' && (
              <RoleTemplates onApplyTemplate={handleApplyTemplate} />
            )}

            {activeTab === 'activity' && (
              <ActivityLog />
            )}

            {activeTab === 'security' && (
              <SecuritySettings />
            )}

            {activeTab === 'stats' && (
              <UserStats users={users} />
            )}
          </div>
        </div>
      </div>
      {/* User Form Modal */}
      <UserForm
        user={editingUser}
        onSave={handleSaveUser}
        onCancel={() => {
          setIsUserFormOpen(false);
          setEditingUser(null);
        }}
        isOpen={isUserFormOpen}
      />
    </div>
  );
};

export default UserManagement;